import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { useInternalDeliveryFacilityLockGetFacilityLocks } from 'api/internal-delivery-facility-lock/internal-delivery-facility-lock';
import { InternalDeliveryFacilityLockDTO } from 'api/model';

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

interface Options {
  /**
   * Till-anläggning och starttid förfrågan redan har (vid redigering). Ligger förfrågan kvar på
   * samma anläggning och samma dag räknas den inte som spärrad — backenden nekar bara när
   * till-anläggningen eller startdagen byts till en låst.
   */
  originalToCompanyId?: number | null;
  originalTargetStartTime?: Date | null;
}

/**
 * Är vald till-anläggning låst för annonsförfrågningar på förfrågans startdag? Backenden svarar
 * med samma regel som AssignmentRequestLogic.EnsureToCompanyNotLocked — manuellt lås eller dagens
 * budget förbrukad, i dag och alla dagar framåt till midnatt — så att spara-knappen kan stängas av
 * i förväg i stället för att backenden nekar.
 */
export const useToCompanyLock = (
  toCompanyId: number | null | undefined,
  targetStartTime: Date | null | undefined,
  { originalToCompanyId, originalTargetStartTime }: Options = {}
): {
  isLocked: boolean;
  lock?: InternalDeliveryFacilityLockDTO;
} => {
  // Dagens början som nyckel, så att varje minutändring i tidsfältet inte blir en ny fråga.
  const dayKey = targetStartTime ? startOfDay(targetStartTime).getTime() : 0;
  const locks = useInternalDeliveryFacilityLockGetFacilityLocks(
    dayKey ? { date: new Date(dayKey) } : undefined,
    { query: { staleTime: 1000 * 30, enabled: !!toCompanyId } }
  );

  const originalDayKey = originalTargetStartTime
    ? startOfDay(originalTargetStartTime).getTime()
    : undefined;

  return useMemo(() => {
    const unchanged =
      toCompanyId === originalToCompanyId && dayKey === originalDayKey;
    if (!toCompanyId || unchanged) {
      return { isLocked: false };
    }
    const lock = locks.data?.find((l) => l.companyId === toCompanyId);
    return { isLocked: !!lock, lock };
  }, [dayKey, locks.data, originalDayKey, originalToCompanyId, toCompanyId]);
};

const Warning = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${({ theme }) => theme.colors.foreground.error};
  font-weight: 500;
  white-space: nowrap;
`;

interface Props {
  lock?: InternalDeliveryFacilityLockDTO;
}

/** Varningen bredvid till-anläggningens dropdown. Renderar inget när anläggningen inte är låst. */
const FacilityLockWarning: FC<Props> = ({ lock }) => {
  if (!lock) return null;

  const title = lock.isBudgetExhausted
    ? 'Dagens annonsbudget för anläggningen är förbrukad. Låset släpper vid midnatt, eller när dagsbudgeten höjs.'
    : `Låst av ${lock.lockedByName ?? 'okänd'}${
        lock.lockedAt
          ? ` kl ${lock.lockedAt.toLocaleTimeString('sv-SE', {
              hour: '2-digit',
              minute: '2-digit',
            })}`
          : ''
      }. Låset släpper vid midnatt.`;

  return (
    <Warning title={title}>
      <FontAwesomeIcon icon={faLock} />
      {lock.isBudgetExhausted
        ? 'Budgeten förbrukad — låst till midnatt'
        : 'Låst för annonsförfrågningar till midnatt'}
    </Warning>
  );
};

export default FacilityLockWarning;
